import { LitElement, css, html } from '@lit'
import * as paths from '@nova/paths'
import { t } from '@nova/utils'
class NovaSearchForm extends LitElement {
  static properties = {
    url: { type: String },
    value: { type: String },
    searchDrawer: { type: Object },
    searchData: { type: String },
    searchTerm: { type: String },
  }

  constructor() {
    super()
    this.searchData = ''
    this.searchTerm = ''

    this.searchDrawer = null
  }

  connectedCallback() {
    super.connectedCallback()
    this.searchDrawer = this.closest('sl-drawer')
  }

  firstUpdated(changedProperties) {
    super.firstUpdated(changedProperties)
    const searchInput = this.shadowRoot.querySelector('.header-search__input')
    searchInput.addEventListener('input', this.searchOnChange.bind(this))
    if (this.value) {
      this.getSearchResults(this.value)
    }
  }

  searchOnChange(event) {
    if (!this.searchDrawer || !this.searchDrawer.open) return
    const searchValue = event.target.value
    this.value = searchValue
    clearTimeout(this.debounceTimeout)
    this.debounceTimeout = setTimeout(() => {
      this.getSearchResults(searchValue)
    }, 500)
  }

  async getSearchResults(searchTerm) {
    if (!searchTerm.length) {
      this.searchData = ''
      this.searchTerm = ''

      this.requestUpdate()
      return
    }

    try {
      const response = await fetch(`/search/suggest?q=${encodeURIComponent(searchTerm)}&resources[limit_scope]=each&resources[options][fields]=body,product_type,tag,title,variants.title,variants.sku,variants.barcode,vendor&resources[type]=query,product,collection&section_id=predictive-search`)
      if (!response.ok) {
        console.error(`HTTP error! Status: ${response.status}`)
        this.searchData = ''
        this.searchTerm = ''
        return
      }
      const data = await response.text()
      this.searchData = data
      this.searchTerm = searchTerm
    } catch (error) {
      console.error('Error:', error.message)
      this.searchData = ''
      this.searchTerm = ''
      return
    }

    this.requestUpdate()
  }

  handleCloseClick() {
    if (this.searchDrawer) {
      this.searchDrawer.hide()
    }
  }

  handleSearchClick() {
    if (!this.value || this.value.trim() === '') return
    this.shadowRoot.querySelector('form').submit()
  }

  render() {
    return html`
      <form
        class="header-search__form"
        action=${this.url}
        method="get"
        role="search"
        aria-label=${t('general.search.search_open')}
      >
        <div class="field header-search__field">
          <sl-input
            class="header-search__input h4"
            id="Search-In-Modal"
            type="search"
            name="q"
            value=${this.value}
            label=${t('general.search.press_enter_to_search')}
            placeholder=${t('general.search.enter_your_search')}
            autocomplete="off"
          ></sl-input>
          <input type="hidden" name="options[prefix]" value="last" />
          <button
            type="button"
            class="header-search__search-button header-search__button"
            @click=${this.handleSearchClick}
            aria-label="${t('general.search.search')}"
          >
            <sl-icon name="search" slot="suffix" class="header-search__search-icon">
            </sl-icon>
          </button>
          <button
            type="button"
            class="header-search__close-button header-search__button"
            @click=${this.handleCloseClick}
            aria-label="${t('general.search.close')}"
          >
            ${paths.close}
          </button>
        </div>
      </form>
      <nova-search-result
        .searchData=${this.searchData}
        .searchTerm=${this.searchTerm}
      ></nova-search-result>
    `
  }

  static styles = css`
    :host {
      width: 100%;
      overflow: auto;
      -ms-overflow-style: none;
      scrollbar-width: none;
    }

    :host::-webkit-scrollbar {
      display: none;
      width: 0;
    }

    :host .header-search__form {
      width: auto;
      padding: var(--nova-space-sm) var(--nova-space-2xs);
      border: 1px solid var(--nova-brand-grey-100);
    }

    :host .header-search__field {
      display: flex;
      align-items: center;
      padding: 0;
    }

    :host .header-search__input {
      width: 100%;
      border: none;
      font: var(--nova-body-font);
    }

    :host .visually-hidden {
      position: absolute !important;
      overflow: hidden;
      width: 1px;
      height: 1px;
      margin: -1px;
      padding: 0;
      border: 0;
      clip: rect(0 0 0 0);
      word-wrap: normal !important;
    }

    :host .header-search__label {
      display: block;
      padding: var(--nova-space-sm) 0 var(--nova-space-md);
      color: var(--nova-color-subdued);
    }

    :host .header-search__input:focus-visible {
      outline: none;
    }

    :host .header-search__input::part(base),
    :host .header-search__input::part(input) {
      height: 19px;
      border: none;
      box-shadow: none;
      border-radius: 0;
    }

    :host .header-search__input::part(input) {
      padding: 0 0 0 var(--nova-space-2xs);
      font-size: 14px;
    }

    :host .header-search__input::part(input)::placeholder {
      color: var(--nova-brand-ink);
      font: var(--nova-body-font);
    }

    .header-search__close-button {
      margin-right: var(--nova-space-2xs);
    }

    :host .header-search__input::part(form-control-label) {
      position: absolute !important;
      overflow: hidden;
      width: 1px;
      height: 1px;
      margin: -1px;
      padding: 0;
      border: 0;
      clip: rect(0 0 0 0);
      word-wrap: normal !important;
    }

    .header-search__button {
      border: none;
      background: none;
      height: 19px;
      width: 19px;
      padding: 0;
      color: var(--nova-brand-ink);
      display: flex;
      align-items: center;
      justify-content: center;
      margin-left: 12px;
      cursor: pointer;

      & sl-icon {
        width: 19px;
        height: 19px;
      }
    }

    /* Clear default clear button from Internet Explorer */
    :host input[type='search']::-ms-clear {
      display: none;
      -webkit-appearance: none;
      width: 0;
      height: 0;
    }

    /* Clear default clear button from Chrome */
    :host input[type='search']::-webkit-search-cancel-button {
      display: none;
      -webkit-appearance: none;
    }

    @media screen and (min-width: 990px) {
      :host .header-search__form {
		    padding: var(--nova-space-md) var(--nova-space-xs-1);
      }

      .header-search__button {
        margin-left: var(--nova-space-xs-1);
      }

      .header-search__close-button {
        margin-right: var(--nova-space-xs-1);
      }

      :host .header-search__input::part(input) {
        padding: 0 0 0 var(--nova-space-xs-1);
      }
    }
  `
}

customElements.define('nova-search-form', NovaSearchForm)
